/**
 * @file shop-fonts.ts
 * @description Свои шрифты магазина в кабинете (phase19.16 Р1-7; SB99, SB103,
 * фаза §7.12): DTO сервера `admin/site-view/fonts`, перевод в запись реестра
 * shared (`ShopFontEntry`), места начертаний, проверка файла до отправки,
 * «Осталось: …» в подвале окна загрузки. «Где используется» —
 * `shop-font-usage.ts`, начертание заголовков — `font-weights.ts`.
 *
 * Всё — чистые функции и реестры (OCP): новое место начертания или роль —
 * новая запись, а не ветка в компоненте. Тексты ошибок сервера —
 * `shop-font-errors.ts`.
 */

import {
  shopFontCssFamily,
  shopFontKey,
  SITE_LIMITS,
  type ShopFontEntry,
  type ShopFontWeight,
  type SiteFontCategory,
} from "@chaiberi/shared";

// ── DTO сервера ──

export type ShopFontCategoryDto = "SERIF" | "SANS" | "SCRIPT" | "DISPLAY";
export type ShopFontStyleDto = "NORMAL" | "ITALIC";

/** Начертание своего шрифта (файл woff2 на сервере). */
export interface ShopFontFileDto {
  id: string;
  weight: ShopFontWeight;
  style: ShopFontStyleDto;
  sizeBytes: number;
  sourceFormat?: string;
  hasCyrillic: boolean;
  /** Путь от корня сервера API (`/api/v1/public/content/media/fonts/<fileId>`). */
  url: string;
  rightsConfirmedAt: string;
  rightsConfirmedByName: string | null;
}

/** Где используется шрифт: роли черновика и опубликованного вида, дизайны. */
export interface ShopFontUsage {
  draftRoles: string[];
  publishedRoles: string[];
  designs: Array<{ id: string; name: string }>;
  /** Стоит в старых версиях вида сайта (только предупреждение). */
  olderRevisions?: boolean;
}

export interface ShopFontDto {
  id: string;
  /** Ключ для оформления: `shop:<id>`. */
  key?: string;
  family: string;
  category: ShopFontCategoryDto;
  sourceNote: string | null;
  hasCyrillic: boolean;
  files: ShopFontFileDto[];
  createdAt: string;
  createdByName?: string | null;
  usedIn: ShopFontUsage;
  /** `false` — шрифт на сайте или в дизайне (удаление вернёт 409 font_in_use). */
  canDelete: boolean;
}

/** Загруженный файл: замечания сервера после перевода в woff2. */
export interface ShopFontUploadedFile {
  fileId: string;
  weight: number;
  style: ShopFontStyleDto;
  variable: boolean;
  hasCyrillic: boolean;
  heavy: boolean;
}

/** Ответ загрузки и догрузки (201): шрифт + замечания по каждому файлу. */
export interface ShopFontUploadResult extends ShopFontDto {
  uploaded?: ShopFontUploadedFile[];
}

export interface ShopFontList {
  items: ShopFontDto[];
  limit: number;
}

/** Итог проверки файла (`POST fonts/inspect`) — ничего не сохраняет. */
export interface FontInspectResult {
  familyName: string | null;
  /** Вид шрифта по файлу; `null` — сервер не понял (необязательное поле). */
  categoryGuess?: ShopFontCategoryDto | null;
  weightGuess: ShopFontWeight;
  styleGuess: ShopFontStyleDto;
  hasCyrillic: boolean;
  variable: boolean;
  sourceFormat: string;
  sourceSizeBytes: number;
  woff2SizeBytes: number;
  heavy: boolean;
}

export interface ShopFontDeleteResult {
  deleted: true;
  /** Роли черновика, которые сервер перевёл на шрифт по умолчанию. */
  draftRoles?: string[];
  /** Шрифт стоял в старых версиях: при возврате к ним будет системный. */
  olderRevisions?: boolean;
}

// ── Категории ──

const CATEGORY_FROM_DTO: Readonly<Record<ShopFontCategoryDto, SiteFontCategory>> = {
  SERIF: "serif",
  SANS: "sans",
  SCRIPT: "script",
  DISPLAY: "display",
};

export function categoryFromDto(category: ShopFontCategoryDto): SiteFontCategory {
  return CATEGORY_FROM_DTO[category] ?? "sans";
}

export function categoryToDto(category: SiteFontCategory): ShopFontCategoryDto {
  return category.toUpperCase() as ShopFontCategoryDto;
}

/** Сегменты «Вид шрифта» в окне загрузки (порядок — как в проекте uiux). */
export const SHOP_FONT_CATEGORY_OPTIONS: ReadonlyArray<{ value: SiteFontCategory; label: string }> = [
  { value: "serif", label: "С засечками" },
  { value: "sans", label: "Без засечек" },
  { value: "script", label: "Рукописный" },
  { value: "display", label: "Декоративный" },
];

/** DTO → запись реестра shared (`shop:<id>`, CSS-имя `cf-<id>`). */
export function toShopFontEntry(dto: ShopFontDto): ShopFontEntry {
  return {
    key: shopFontKey(dto.id),
    family: dto.family,
    cssFamily: shopFontCssFamily(dto.id),
    category: categoryFromDto(dto.category),
    hasCyrillic: dto.hasCyrillic,
    files: dto.files.map((f) => ({ weight: f.weight, style: f.style === "ITALIC" ? "italic" : "normal", url: f.url })),
  };
}

// ── Места начертаний ──

export type FontSlotKey = "regular" | "bold" | "italic" | "boldItalic";

export interface FontSlotMeta {
  key: FontSlotKey;
  /** Подпись места: «Обычный». */
  label: string;
  /** В списке начертаний строки: «жирный». */
  short: string;
  /** В предупреждении «Похоже, это жирное начертание». */
  adjective: string;
  weight: ShopFontWeight;
  style: ShopFontStyleDto;
  /** Имя поля файла в multipart `POST fonts`. */
  field: string;
  required: boolean;
}

export const FONT_SLOTS: readonly FontSlotMeta[] = [
  { key: "regular", label: "Обычный", short: "обычный", adjective: "обычное", weight: 400, style: "NORMAL", field: "regular", required: true },
  { key: "bold", label: "Жирный", short: "жирный", adjective: "жирное", weight: 700, style: "NORMAL", field: "bold", required: false },
  { key: "italic", label: "Курсив", short: "курсив", adjective: "курсивное", weight: 400, style: "ITALIC", field: "italic", required: false },
  { key: "boldItalic", label: "Жирный курсив", short: "жирный курсив", adjective: "жирное курсивное", weight: 700, style: "ITALIC", field: "boldItalic", required: false },
];

export function slotMeta(key: FontSlotKey): FontSlotMeta {
  return FONT_SLOTS.find((s) => s.key === key) ?? FONT_SLOTS[0]!;
}

export function slotOf(weight: number, style: ShopFontStyleDto): FontSlotKey {
  return FONT_SLOTS.find((s) => s.weight === weight && s.style === style)?.key ?? "regular";
}

/** Загруженные места шрифта. */
export function loadedSlots(font: Pick<ShopFontDto, "files">): FontSlotKey[] {
  return FONT_SLOTS.filter((s) => font.files.some((f) => f.weight === s.weight && f.style === s.style)).map((s) => s.key);
}

/** «Обычный, жирный» — начертания в порядке мест. */
export function fontStylesText(font: Pick<ShopFontDto, "files">): string {
  const text = loadedSlots(font).map((key) => slotMeta(key).short).join(", ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Нет начертаний";
}

// ── Проверка файла до отправки ──

const FONT_EXTENSIONS = [".woff2", ".ttf", ".otf"] as const;

/** Подсказка выбора файла (`accept`). */
export const FONT_FILE_ACCEPT = FONT_EXTENSIONS.join(",");

export const FONT_FILE_TEXT = {
  format: "Этот файл не подходит. Нужен файл шрифта: .woff2, .ttf или .otf.",
  invalid: "Файл не читается как шрифт — возможно, он повреждён. Скачайте его заново.",
  size: "Файл больше 2 МБ — такой шрифт замедлит сайт. Возьмите версию .woff2 или файл только с русскими и латинскими буквами.",
} as const;

/** Ошибка файла до отправки на сервер; `null` — можно проверять. */
export function checkFontFile(file: { name: string; size: number }): string | null {
  const name = file.name.toLowerCase();
  if (!FONT_EXTENSIONS.some((ext) => name.endsWith(ext))) return FONT_FILE_TEXT.format;
  if (file.size > SITE_LIMITS.shopFontUploadBytes) return FONT_FILE_TEXT.size;
  return null;
}

/** Предупреждение о весе файла: «Шрифт весит 520 КБ — …». */
export function heavyFontText(bytes: number): string {
  return `Шрифт весит ${Math.round(bytes / 1024)} КБ — страницы будут открываться чуть медленнее. Можно продолжить.`;
}

/** Подпись окна догрузки (текст может поменяться по решению пользователя). */
export const ADD_FILE_NOTE = "Новое начертание появится на сайте в течение минуты";

/**
 * Адрес файла шрифта для образца в кабинете. Сервер отдаёт путь от своего
 * корня; если API на другом origin (`VITE_API_URL` абсолютный) — путь
 * приклеивается к его origin, как у картинок медиатеки. Абсолютный адрес —
 * как есть. В реестре и сообщении канве остаётся исходный путь сервера.
 */
export function cabinetFontFileUrl(url: string, apiBase: string): string {
  if (/^https?:\/\//i.test(url) || !/^https?:\/\//i.test(apiBase)) return url;
  return new URL(url, apiBase).href;
}

export const NO_CYRILLIC_TEXT = "В шрифте нет русских букв — русский текст на сайте покажется запасным шрифтом.";
export const VARIABLE_FONT_TEXT = "Это шрифт с плавной толщиной — пока используем его как обычное начертание";
const HEAVY_AFTER_UPLOAD_TEXT = "Шрифт тяжёлый — страницы будут открываться чуть медленнее.";

/**
 * Замечания сервера после загрузки (`uploaded[]`), которых не было на
 * проверке файла: «Жирный: в шрифте нет русских букв…». `inspected` —
 * итог проверки места (`null` — проверки не было).
 */
export function lateUploadWarnings(
  uploaded: readonly ShopFontUploadedFile[],
  inspected: (slot: FontSlotKey) => FontInspectResult | null,
): string[] {
  const out: string[] = [];
  for (const file of uploaded) {
    const slot = slotOf(file.weight, file.style);
    const info = inspected(slot);
    const notes = [
      !file.hasCyrillic && (info?.hasCyrillic ?? true) ? NO_CYRILLIC_TEXT : null,
      file.heavy && !info?.heavy ? HEAVY_AFTER_UPLOAD_TEXT : null,
      file.variable && !info?.variable ? VARIABLE_FONT_TEXT : null,
    ].filter((n): n is string => n !== null);
    for (const note of notes) out.push(`«${slotMeta(slot).label}»: ${note}`);
  }
  return out;
}

// ── Подвал окна загрузки ──

export interface UploadReadiness {
  mode: "create" | "addFile";
  family: string;
  /** Обычное начертание проверено (create) / есть новое проверенное (addFile). */
  hasRequiredFile: boolean;
  consent: boolean;
}

/** Чего не хватает для «Загрузить» — только недостающее. */
export function missingUploadSteps(r: UploadReadiness): string[] {
  const steps: string[] = [];
  if (r.mode === "create" && !r.family.trim()) steps.push("указать название");
  if (!r.hasRequiredFile) steps.push(r.mode === "create" ? "добавить обычное начертание" : "добавить начертание");
  if (!r.consent) steps.push("отметить право использования");
  return steps;
}
